import * as React from 'react';
import { Transforms, Editor, createEditor,  Text as SlateText, Point, Element, Node } from 'slate'
import { ReactEditor, withReact, Slate } from 'slate-react'
import { renderToStaticMarkup } from 'react-dom/server';
import {
   escapeRegExp, 
   getText, 
   getLastNode, 
   findNode,
   getNodes,
   unwrapNodes,
   wrapNodes, 
   ELEMENT_ALIGN_LEFT,
   ELEMENT_ALIGN_CENTER,
   ELEMENT_ALIGN_RIGHT,
   ELEMENT_ALIGN_JUSTIFY,
} from '@udecode/slate-plugins';
import { QueryClientProvider,QueryClient } from 'react-query';
import produce from 'immer';

import { EditorProvider, getParticipantsMap } from '../../editor-context'
import { deserializeHTML } from './TratoFormat'
import { MAX_TABLE_COLS, MAX_TABLE_ROWS, BLOCK_TABLE_ROW, BLOCK_TABLE_CELL, BLOCK_TABLE_WRAP } from './Elements'
import { ProfileProvider } from 'context/profile'
import { getMongoObjectId } from 'utils'

const queryClient = new QueryClient()

export function isAlignBlock(type) {
    return ['align_justify', 'justify', 'align_left', 'align_center', 'align_right'].includes(type);
}

export function getFullDocumentRange(editor) {
  const [lastNode, lastNodePath] = getLastNode(editor, 1)

  return {
    anchor: { path: [0], offset: 0 },
    focus: { path: lastNodePath, offset: lastNode?.text?.length || 0 },
  };
}

export function getNodesByType(editor, type, options = {}) {
  const { mode = 'all', match = () => true } = options;

  return Editor.nodes(editor, {
      at: [],
     mode,
     match: (n) => {
       if (isAlignBlock(n.type)) {
           return n.children.some(n2 => n2.type === type) && match(n);
       }

       return n.type === type && match(n);
     }
   })
}
const getWordAfterTrigger = (editor, { at, wordBefore }) => {
  const wordBeforeSpace = Editor.before(editor, at, { unit: 'word' });
  // Point before wordBefore
  const before = wordBeforeSpace && Editor.before(editor, wordBeforeSpace);

  // Range from before to start
  const beforeRange = before && Editor.range(editor, before, at);

  return { text: getText(editor, beforeRange), wordBefore: wordBeforeSpace };
}

export const isWordAfterTrigger = (
  editor,
  { at, trigger, spaceChar }
) => {
  // Point at the start of previous word (excluding punctuation)
  try {
    
  const wordBefore = Editor.before(editor, at, { unit: 'word' });
  // console.log({ at, wordBefore,})
  // Point before wordBefore
  const before = wordBefore && Editor.before(editor, wordBefore);

  // Range from before to start
  let beforeRange = before && Editor.range(editor, before, at);

  // Before text
 
  const beforeText = getText(editor, beforeRange);
  const wordArr = [beforeText]

  if (beforeText.startsWith(spaceChar)) {
    // accumulatedText += beforeText;\
    let shouldContinue = true;
    let wordAt = wordBefore;
    do {
      const { wordBefore: newWordBefore, text } = getWordAfterTrigger(editor, { at: wordAt });
      
      wordAt = newWordBefore
      shouldContinue = text.startsWith(spaceChar)
      wordArr.unshift(text)
 
    } while(shouldContinue);
    const start = {
      path: wordAt.path,
      offset: wordAt.offset - 1
    }
    beforeRange = Editor.range(editor, start, at)
  }

  // Starts with char and ends with word characters
  const escapedTrigger = escapeRegExp(trigger);

  const beforeRegex = new RegExp(`^${escapedTrigger}([\\w|À-ÖØ-öø-ÿ|_]+)$`);

  // console.log({ at, wordBefore, before, beforeRange, beforeText, beforeRegex })
  // Match regex on before text
  const word = wordArr.join('').replace(/__/ig, '_')

  const match = !!word && word.match(beforeRegex);

  return {
    range: beforeRange,
    match
  }

  } catch (error) {
    return {
      range: { 
        anchor: { path: [0, 0], offset: 0 },
        focus: { path: [0, 0], offset: 0 }
      },
      match: false
    }
  }
}

export function updateAllTextNodes(editor, properties = {}, options) {
  const { specialTextNodes = false } = options;
  const fullDocumentRange = getFullDocumentRange(editor);

  const nodes = Array.from(Editor.nodes(editor, {mode: 'highest', match: (n) => Element.isElement(n),  at: fullDocumentRange }));

  nodes.map(([node, nodeLocation]) => {
    const children = Array.from(Node.texts(node));

    children.map(([,childrenLocation]) => {
      Transforms.setNodes(editor, properties, { mode: 'lowest', at: [...nodeLocation, ...childrenLocation ]})
    })
  })

  if (specialTextNodes) updateSpecialTextNodes(editor, properties, { at: fullDocumentRange })
  
} 

export function updateSpecialTextNodes(editor, properties = {}, options = {}) {
  Transforms.setNodes(editor, properties, {
    mode: 'all',
    voids: true,
    match: (n) => ['variable', 'statement', 'clause', 'li'].includes(n.type),
    ...options
  })
  
} 


export function insertNodeAndScroll(editor, node) {
    Transforms.insertNodes(editor, node, {
      at: [editor.children.length]
    })
    const lastElement = editor.children[editor.children.length - 1]
    scrollToNode(editor, lastElement);
}

export function scrollToNode(editor, node) {
  setTimeout(() => {
      const elementDom = document.querySelector(`[data-element-id="${node._id}"]`)
      elementDom?.scrollIntoView({ behavior: 'smooth',  block: 'center' })
      const [, path] = findNode(editor, { at: [], match: (n) => n._id === node._id}) ?? []
      if (path) {
        Transforms.select(editor, path)
        Transforms.move(editor)
      }

  }, 300)
}



export const getNodesById = (editor, id) => {
    const ids = Array.isArray(id) ? id : [id];
  
    return [
      ...Editor.nodes(editor, {
        mode: 'highest',
        match: (n) => {
          const nodeId = n._id;
          return !!nodeId && ids.includes(nodeId);
        },
        at: [],
      }),
    ];
  };


export const removeNodesById = (editor, id) => {
  const ids = Array.isArray(id) ? id : [id];
  const nodes = getNodesById(editor, id)
  // nodes.map(([node, path]) => {
  //   Transforms.removeNodes(editor, {
  //     mode: 'highest',
  //     at: path,
  //     voids: true
  //   })
  // })
  try {
    Transforms.removeNodes(editor, {
      mode: 'highest',
      match: (n) => {
        const nodeId = n._id;
        return !!nodeId && ids.includes(nodeId);
      },
      at: [],
      voids: true
    })
  } catch (error) {
    
  }
    
  
};

export const removeNodesByProperty = (editor, {
  property,
  value
}) => {

  Transforms.removeNodes(editor, {
    mode: 'highest',
    match: (n) => {
      const nodeProperty = n[property];
      return nodeProperty === value;
    },
    at: [],
  })
  
};

export const getNodesByProperty = (editor, {
  property,
  value
}) => {

  return [
    ...Editor.nodes(editor, {
      mode: 'highest',
      match: (n) => {
        const nodeProperty = n[property];
        return nodeProperty === value;
      },
      at: [],
    }),
  ];
};

export const isMarkActive = (editor, type) => {
  try {
    const marks = Editor.marks(editor);
    return marks ? marks[type] === true : false;
  } catch (error) {
    return false
  }
};

export const upsertAlign = (
  editor,
  {
    type = 'left'
  }
) => {
  if (!editor.selection) return;

  const nodes = Array.from(getNodes(editor, {mode: 'highest', match: (n) => Element.isElement(n)}));

  nodes.map(([node, nodeLocation]) => {
      const isElement = Element.isElement(node);

      if (isElement) {
          // unwrapNodes(editor, { match: { type: unwrapTypes }, at: nodeLocation });

          if (!type) return;
          Transforms.setNodes(editor, {
            textAlign: type
          }, {
            at: nodeLocation
          })
        
          // wrapNodes(
          //   editor,
          //   {
          //     type,
          //     children: [],
          //   },
          //   { mode: 'lowest', at: nodeLocation }
          // );
      }
  })
 

};

export const isAlignActive = (editor, { type = 'left' }) => {
  if (!editor.selection) return false;
  if (!editor.children?.length) return false;

  const { anchor } = editor.selection
  const { path } = anchor
  if (!path) return false;

  const [element] = findNode(editor, {
      at: [path[0]]
  })

  
  return element.textAlign === type;
}


function parseVariables(elementsArr = []) {
  const elements = Array.isArray(elementsArr) ? elementsArr : [elementsArr];
  if (!elements.length) return elements;

  return elements.map(el => {
    if (el.type === 'variable') {
      const { text, ...rest } = el;
      return [{text: ' '}, {
        ...rest,
        children: [{ text: ' ' }]
      }, {text: ''}]; 
    } else if (el.type === 'image') {
      const { text, ...image } = el;
      return [{text: ' '}, image, {text: ''}]; 
    }

    return el;
  }).flat();
}

export function migrateElements(elements = [], { contract }) {
  const participants = getParticipantsMap(contract.participants);

  return elements?.map((el) => {
    if (el.type === 'signature') {
      const parsedSignature = deserializeHTML(el.content, { contract, participants }).find(c => c.type === 'signature');

      return {
        ...el,
        participants: el.participants.length ? el.participants : (parsedSignature?.participants ?? []),
        children: [{ text: '', }]
      }
    }
    if (el.type === 'document_logo') {
      const parsedLogo = deserializeHTML(el.content, { contract, participants });
      const imageFound = parsedLogo.find(p => p.type === 'image')

      if (imageFound) {
        const image = {
          name: 'logo',
          type: 'image',
          width: 150,
          url: imageFound.url,
          children: [{ text: '' }]
        }

        return {
          ...el,
          type: 'text',
          children: [
            { text: '' },
            image,
            { text: '' }
          ]
        }
      }

      return {
        ...el,
        type: 'text',
        children: parsedLogo
      }


    }
    if (el.type === 'header' && !el.staticfield) {
      return {
        ...el,
        children: [{ text: '' }]
      }
    }
    if (el.type === 'header' && el.staticfield) {
      const parsedHeader = deserializeHTML(el.content, { contract, participants });

      return {
        ...el,
        type: 'static-header',
        children: parseVariables(parsedHeader.map(elOrChildren => {
          if (isAlignBlock(elOrChildren.type)) {
            return elOrChildren.children
          }
          elOrChildren.lineHeight = '1.5';

          return elOrChildren
        }).flat())
      }
    }

    if (el.content?.toLowerCase().includes('<table') || el.content?.toLowerCase().includes('<ol') || el.content?.toLowerCase().includes('<ul')) {  
      const parseContent = deserializeHTML(el.content, { contract, participants });
      let hasAppliedId = false;
      // console.log({parseContent})
      const elements = [];
      let currentElement = {
        ...el,
        isInitial: true,
        children: []
      }


      parseContent.map(elOrChildren => {

        let id = getMongoObjectId();
  
        if (elOrChildren.type === 'table') {
          if (currentElement.children.length) { 
            elements.push(currentElement)
            currentElement = {
              _id: getMongoObjectId(),
              type: 'text',
              isInitial: false,
              children: [{text: ''}]
            }
          } 
          const rowCount = elOrChildren.children.length;
          const columnCount = elOrChildren.children.reduce((max, row) => {
            let total = 0;
            row.children?.map(col => {
              total += col.colspan
            })
            if (total > max) max = total;
            return max;
          }, 0)
          
          const table = produce(elOrChildren, (draft) => {
            draft.row = Math.min(rowCount, MAX_TABLE_ROWS);
            draft.col = Math.min(columnCount, MAX_TABLE_COLS);
            draft.children = draft.children.filter(el => el.type === BLOCK_TABLE_ROW).filter((el, index) => index < MAX_TABLE_ROWS).filter(el => el?.children?.length)
  
            draft.children.forEach(el => {
              el.children = el.children.filter((el) => el.type === BLOCK_TABLE_CELL).filter((el, index) => index < MAX_TABLE_COLS)
              el.children.forEach(el2 => {
                el2.children.filter(el => el.text === BLOCK_TABLE_CELL)
                const textNodes = el2.children.map(el3 => ({ ...el3, text: (el3?.text ?? '').replace(/↵/ig, '').trim() })).filter(el3 => el3.text.length > 0 || ['variable', 'image'].includes(el3.type)).map(c => ({
                  type: 'p',
                  children: [{ text: ' ' }, c, { text: ' ' }]
                })).map((n) => ({ ...n, children: parseVariables(n.children) }));
                el2.children = !textNodes.length ? [{ type: 'p', text:''}] : textNodes
                
              })
            })
         
        })
          
        elements.push({
          _id: getMongoObjectId(),
          type: BLOCK_TABLE_WRAP,
          row: rowCount,
          col: columnCount,
          children: [
            // {
            //   type: BLOCK_TABLE_PRE,
            //   children: [{ type: 'p', children: [{ text: '' }] }]
            // },
            table,
            // {
            //   type: BLOCK_TABLE_SUF,
            //   children: [{ text: '' }]
            // },
          ]
        })
        }
        
        if (elOrChildren.type === 'ol' || elOrChildren.type === 'ul') {
          
          if (currentElement.children.length) { 
            elements.push(currentElement)
            currentElement = {
              _id: getMongoObjectId(),
              type: 'text',
              isInitial: false,
              children: [{text: ''}]
            }
          } 
          elOrChildren.children = produce(elOrChildren.children, (draft) => {
  
            draft.forEach(el => {
              if (el.type === 'li') {
                el.children.forEach(el2 => {
                  if (el2.type === 'p') {
                    el2.children.forEach(el3 => {
                      el3.lineHeight = '1.5';
                    })
                    el2.children = parseVariables(el2.children)
                  } else {
                    el2.lineHeight = '1.5';
                  }
                })
              } else {
                el.lineHeight = '1.5';
              }
            })
          })
          elements.push({ _id: getMongoObjectId(), ...elOrChildren })
          return;
        }
        
        if (isAlignBlock(elOrChildren.type)) {
          // elOrChildren.children.map(c => currentElement.children.push)
          currentElement.children.push(parseVariables(elOrChildren.children));
          currentElement.children = currentElement.children.flat()
          return ;
        }
        elOrChildren.lineHeight = '1.5';
        // console.log({elOrChildren})

        currentElement.children.push(parseVariables(elOrChildren))
        currentElement.children = currentElement.children.flat()
      });

      if (!currentElement.isInitial) elements.push(currentElement);

      return elements;
    }

    const parsedContent = deserializeHTML(el.content, { contract, participants });
    
    return {
      ...el,
      children: parseVariables(parsedContent.map(elOrChildren => {
        if (isAlignBlock(elOrChildren.type)) {
          return elOrChildren.children
        }
        elOrChildren.lineHeight = '1.5';
        return elOrChildren
      }).flat())
    }

    // return {
    //   ...el,
    //   children: deserializeHTML(el.content, { contract, participants })
    // }
  }).flat()
}



export const createElementWithSlate = (slateProps) => {
  const {
    editor = withReact(createEditor()),
    value = [],
    onChange = () => {},
    children,
    ...props
  } = slateProps || {};

  return React.createElement(
    Slate,
    {
      editor,
      value,
      onChange,
      ...props,
    },
    children
  )

};

// Remove extra whitespace generated by ReactDOMServer
const trimWhitespace = (rawHtml) =>
  rawHtml.replace(/(\r\n|\n|\r|\t)/gm, '');

// Remove redundant data attributes
const stripSlateDataAttributes = (rawHtml) =>
  rawHtml
    .replace(/( data-slate)(-node|-type)="[^"]+"/gm, '')
    .replace(/( data-testid)="[^"]+"/gm, '');

/**
 * Remove all class names that are not starting with `slate-`
 */
const stripClassNames = (html) => {
  const allClasses = html.split(/(class="[^"]*")/g);

  let filteredHtml = '';
  allClasses.forEach((item, index) => {
    if (index % 2 === 0) {
      return (filteredHtml += item);
    }
    const slateClassNames = item.match(/(slate-[^"\s]*)/g);
    if (slateClassNames) {
      filteredHtml += `class="${slateClassNames.join(' ')}"`;
    }
  });

  return filteredHtml;
};

const getNode = ({
  plugins,
  elementProps,
  slateProps,
}) => {
  // If no type provided we wrap children with div tag
  if (!elementProps.element.type) {
    return `<div>${elementProps.children}</div>`;
  }

  let html;

  // Search for matching plugin based on element type
  plugins.some((plugin) => {
    if (!plugin.serialize?.element && !plugin.renderElement) return false;
    if (
      !plugin.deserialize?.element?.some(
        (item) => item.type === String(elementProps.element.type)
      )
    ) {
      html = `<div>${elementProps.children}</div>`;
      return false;
    }

    // Render element using picked plugins renderElement function and ReactDOM
    html = renderToStaticMarkup(
      createElementWithSlate({
        ...slateProps,
        children:
          plugin.serialize?.element?.(elementProps) ??
          plugin.renderElement?.(elementProps),
      })
    );

    html = stripClassNames(html);

    return true;
  });

  return html;
};

const getLeaf = ({
  plugins,
  leafProps,
  slateProps,
}) => {
  const { children } = leafProps;

  return plugins.reduce((result, plugin) => {
    if (!plugin.serialize?.leaf && !plugin.renderLeaf) return result;
    if (
      (plugin.serialize?.leaf?.(leafProps) ??
        plugin.renderLeaf?.(leafProps)) === children
    )
      return result;

    const newLeafProps = {
      ...leafProps,
      children: encodeURIComponent(result),
    };

    let html = decodeURIComponent(
      renderToStaticMarkup(
        createElementWithSlate({
          ...slateProps,
          children:
            plugin.serialize?.leaf?.(leafProps) ??
            plugin.renderLeaf?.(newLeafProps),
        })
      )
    );

    html = stripClassNames(html);

    return html;
  }, children);
};

const isEncoded = (str = '') => {
  try {
    return str !== decodeURIComponent(str);
  } catch (error) {
    return false;
  }
};

/**
 * Convert Slate Nodes into HTML string
 */
export const serializeHTMLFromNodes = ({
  plugins,
  nodes,
  slateProps,
  stripDataAttributes = true,
}) => {
  let result = nodes
    .map((node) => {
      if (SlateText.isText(node)) {
        return getLeaf({
          plugins,
          leafProps: {
            leaf: node,
            text: node,
            children: isEncoded(node.text)
              ? node.text
              : encodeURIComponent(node.text),
            attributes: { 'data-slate-leaf': true },
          },
          slateProps,
        });
      }
      return getNode({
        plugins,
        elementProps: {
          element: node,
          children: encodeURIComponent(
            serializeHTMLFromNodes({
              plugins,
              nodes: node.children,
            })
          ),
          attributes: { 'data-slate-node': 'element', ref: null },
        },
        slateProps,
      });
    })
    .join('');

  result = trimWhitespace(decodeURIComponent(result));

  if (stripDataAttributes) {
    result = stripSlateDataAttributes(result);
  }

  return result;
};